/**
 * Extracts a human-readable error message from the variety of error shapes the
 * billing backend (and JS runtimes) can produce.
 *
 * Priority, per error argument:
 *   1. string that JSON-parses to an object → treated as the object case
 *      below (the backend sometimes hands us a stringified problem-detail)
 *   2. plain string                       → returned as-is
 *   3. object with `detail`      (string) → returned (RFC-7807 problem-detail)
 *   4. object with `title`       (string) → returned (legacy `/alerts/billing`
 *      and similar endpoints that pre-date RFC-7807 use `{ title, description }`)
 *   5. object with `description` (string) → returned (same legacy shape)
 *   6. object with `message`     (string) → returned (Error instances, etc.)
 *   7. object with `error`       (string) → returned (nested `{ error: "..." }`)
 *
 * Anything else (including a stringified `[object Object]`) is skipped so the
 * caller can supply its own fallback copy.
 *
 * Server (5XX) errors short-circuit everything: the billing gateway returns an
 * HTML error page rather than a JSON problem-detail, so surface a friendly
 * message instead of dumping raw HTML into the UI.
 *
 * Variadic: the first argument that yields a non-empty message wins. Useful
 * when a hook exposes multiple parallel error sources.
 */

export const BILLING_SERVICE_UNAVAILABLE_MESSAGE =
  "Service is temporarily unavailable, please refresh the page or try again in a few minutes.";

const tryParseJson = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

// A 5XX comes back either as an HTML error page (a string body with no
// parseable status) or, when the gateway emits JSON, as an object carrying a
// 5xx `status`.
const isServerError = (value: unknown): boolean => {
  if (typeof value === "string") {
    const start = value.trimStart().toLowerCase();
    return start.startsWith("<!doctype") || start.startsWith("<html");
  }

  if (typeof value === "object" && value !== null) {
    const status = (value as Record<string, unknown>).status;
    return typeof status === "number" && status >= 500 && status < 600;
  }

  return false;
};

/**
 * Reads the HTTP status off a billing error, handling the same shapes as
 * `getNewBillingSystemError`: the backend sometimes hands us a stringified
 * problem-detail, otherwise a parsed object. Returns undefined when no numeric
 * `status` is present (e.g. an HTML error page or a bare Error).
 */
export const getBillingErrorStatus = (err: unknown): number | undefined => {
  const normalized = typeof err === "string" ? (tryParseJson(err) ?? err) : err;

  if (typeof normalized === "object" && normalized !== null) {
    const status = (normalized as Record<string, unknown>).status;
    if (typeof status === "number") return status;
  }

  return undefined;
};

export const getNewBillingSystemError = (
  ...errors: unknown[]
): string | undefined => {
  for (const error of errors) {
    if (!error) continue;

    const normalized =
      typeof error === "string" ? (tryParseJson(error) ?? error) : error;

    if (isServerError(normalized)) return BILLING_SERVICE_UNAVAILABLE_MESSAGE;

    if (typeof normalized === "string") return normalized;

    if (typeof normalized === "object" && normalized !== null) {
      const err = normalized as Record<string, unknown>;
      if (typeof err.detail === "string") return err.detail;
      if (typeof err.title === "string") return err.title;
      if (typeof err.description === "string") return err.description;
      if (typeof err.message === "string") return err.message;
      if (typeof err.error === "string") return err.error;
    }
  }

  return undefined;
};
